import {action, reaction, IReactionDisposer} from 'mobx';
import {ClientRoomStorage} from './ClientRoomStorage';
import {IChatApi} from '../../../services/ts-chat-api/ChatApi';
import {DashboardState} from './DashboardState';
import {UserType} from '@techsee/techsee-common/lib/constants/room.constants';
import get from 'lodash/get';

export class RoomStorageService {
    private _disposers: IReactionDisposer[] = [];

    constructor(
        private _chatApi: IChatApi,
        private _dashboardState: DashboardState,
        private _clientState: ClientRoomStorage = new ClientRoomStorage()
    ) {
        this._chatApi.on('sync', this._onSync);
        this._chatApi.on('statusChanged', this._statusChanged);

        this._disposers.push(
            reaction(
                () => this._dashboardState.isLiveVideo,
                (isLive) => {
                    if (
                        isLive &&
                        !this._clientState.zoomCapability &&
                        get(this._dashboardState.accountData, 'protectedSettings.enableRemoteCameraZoom')
                    ) {
                        this._chatApi.requestAction('getZoomCapability');
                    }
                }
            ),
            reaction(
                () => this._clientState.zoomLevel,
                () => {
                    this._dashboardState.cameraZoomController.clearPendingZoom();
                }
            ),
            reaction(
                () => this._clientState.connected,
                action((connected) => {
                    if (!connected) {
                        this._clientState.zoomCapability = null;
                        this._clientState.zoomLevel = 1;
                    }
                })
            )
            // Note: the agent does NOT reset zoomLevel when zoomCapability
            // changes. Per the CC-4429 spec (§4 camera switch, §5 mode switch),
            // the mobile is the source of truth: on a real camera switch it
            // resets zoom and reports `zoomLevel = 1` itself, and on a
            // photo/video mode switch it re-applies the remembered level. A
            // capability re-report for the same camera must leave the confirmed
            // zoomLevel untouched — otherwise the slider snaps back to 1.
        );
    }

    get clientState(): ClientRoomStorage {
        return this._clientState;
    }

    @action
    private _onSync = (): void => {
        if (this._chatApi.client) {
            Object.keys(this._chatApi.client).forEach((key) => {
                if (key === 'isReviewingTOS') {
                    return;
                }
                if (key in this._clientState) {
                    (this._clientState as any)[key] = this._chatApi.client[key];
                }
            });
        }

        if (this._chatApi.dashboard) {
            Object.keys(this._chatApi.dashboard).forEach((key) => {
                if (key in this._dashboardState.dashboardRoomStorage) {
                    (this._dashboardState.dashboardRoomStorage as any)[key] = this._chatApi.dashboard[key];
                }
            });
        }

        if (this._chatApi.livePointer) {
            Object.keys(this._chatApi.livePointer).forEach((key) => {
                if (key in this._dashboardState.livePointerRoomStorage) {
                    (this._dashboardState.livePointerRoomStorage as any)[key] = this._chatApi.livePointer[key];
                }
            });
        }

        this._dashboardState.isClientRoomStorageSynced = this._chatApi.synced;

        if (this._chatApi.client) {
            this._clientState._isReviewingTOS = this._chatApi.client.isReviewingTOS || false;
        }
    };

    dispose(): void {
        this._chatApi.removeListener('sync', this._onSync);
        this._disposers.forEach((d) => d());
        this._disposers = [];
    }

    @action
    private _statusChanged = (selfStatus: UserType, param: string, value: any): void => {
        if (selfStatus === UserType.client) {
            if (param in this._clientState) {
                (this._clientState as any)[param] = value;
            }
        } else if (selfStatus === UserType.dashboard) {
            if (param in this._dashboardState.dashboardRoomStorage) {
                (this._dashboardState.dashboardRoomStorage as any)[param] = value;
            }
        } else if (selfStatus === UserType.livePointer) {
            if (param in this._dashboardState.livePointerRoomStorage) {
                (this._dashboardState.livePointerRoomStorage as any)[param] = value;
            }
        }
    };
}
